import {
  FilmIcon,
  PhotographIcon,
  PlusCircleIcon,
  SparklesIcon,
  StarIcon,
  VolumeUpIcon,
} from '@heroicons/react/outline';
import Link from 'next/link';
import useLocale from 'next-translate/useTranslation';
import React, { useState } from 'react';

import AutoPlayVideo from '@/components/media/AutoPlayVideo';

const HOME = 'https://media.easy-peasy.ai/landing/home';
const IMAGE = 'https://media.easy-peasy.ai/landing/image';
const VIDEO = 'https://media.easy-peasy.ai/landing/video';
const ASSETS = 'https://media.easy-peasy.ai/assets';

type ToolCard = {
  name: string;
  description: string;
  href: string;
  media: string;
  poster?: string;
  video?: boolean;
};

type ToolCategory = {
  label: string;
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  tools: ToolCard[];
};

// Mirrors the marketing nav groups (Agents / Images / Video / Audio) so the
// homepage tells the same story as the menu — with real output as card art.
const CATEGORIES: ToolCategory[] = [
  {
    label: 'Featured',
    Icon: StarIcon,
    tools: [
      {
        name: 'Marky Agent',
        description: 'Chats with every top model — and does the work',
        href: '/marky',
        media: `${ASSETS}/website-1024-hq.mp4`,
        video: true,
      },
      {
        name: 'AI Image Generator',
        description: 'Photoreal to anime with 40+ models',
        href: '/ai-image-generator',
        media: `${IMAGE}/hero-atelier.webp`,
      },
      {
        name: 'AI Video Generator',
        description: 'Cinematic clips from text or images',
        href: '/ai-video-generator',
        media: `${VIDEO}/glacier.mp4`,
        poster: `${VIDEO}/glacier-poster.jpg`,
        video: true,
      },
      {
        name: 'Video Agent',
        description: 'One sentence in, a finished video out',
        href: '/create-video',
        media: `${VIDEO}/filmset.mp4`,
        poster: `${VIDEO}/filmset-poster.jpg`,
        video: true,
      },
      {
        name: 'AI Headshots',
        description: 'Professional headshots from selfies',
        href: '/ai-headshot-generator',
        media: `${HOME}/img-headshot.webp`,
      },
      {
        name: 'Text to Speech',
        description: 'Lifelike voices in 30+ languages',
        href: '/text-to-speech',
        media: `${HOME}/audio-tts.webp`,
      },
    ],
  },
  {
    label: 'Agents',
    Icon: SparklesIcon,
    tools: [
      {
        name: 'Marky Agent',
        description: 'Chats with every top model — and does the work',
        href: '/marky',
        media: `${ASSETS}/website-1024-hq.mp4`,
        video: true,
      },
      {
        name: 'Video Agent',
        description: 'One sentence in, a finished video out',
        href: '/create-video',
        media: `${VIDEO}/filmset.mp4`,
        poster: `${VIDEO}/filmset-poster.jpg`,
        video: true,
      },
      {
        name: 'Custom AI Agents',
        description: 'Chatbots trained on your own data',
        href: '/chatbots',
        media: `${HOME}/more-customagents.webp`,
      },
      {
        name: 'AI Workflows',
        description: 'Chain models into automated pipelines',
        href: '/ai-workflows',
        media: '/images/workflows.jpeg',
      },
      {
        name: 'Content Templates',
        description: 'One-click generators for any writing task',
        href: '/templates',
        media: `${HOME}/more-templates.webp`,
      },
    ],
  },
  {
    label: 'Images',
    Icon: PhotographIcon,
    tools: [
      {
        name: 'AI Image Generator',
        description: 'Photoreal to anime with 40+ models',
        href: '/ai-image-generator',
        media: `${IMAGE}/hero-atelier.webp`,
      },
      {
        name: 'AI Headshots',
        description: 'Professional headshots from selfies',
        href: '/ai-headshot-generator',
        media: `${HOME}/img-headshot.webp`,
      },
      {
        name: 'Photo Studio',
        description: 'Photo packs of you, in any style',
        href: '/ai-photo-generator',
        media: `${HOME}/img-fashion.webp`,
      },
      {
        name: 'Interior Design',
        description: 'Restyle any room from a photo',
        href: '/ai-interior-design-generator',
        media: `${HOME}/img-interior.webp`,
      },
      {
        name: 'Image Editor',
        description: 'Change anything with a prompt or a brush',
        href: '/ai-image-editor',
        media: '/img/tools/FindReplaceGraphic.jpg',
      },
      {
        name: 'Image Upscaler',
        description: 'Free 4K upscales of any image',
        href: '/free-tools/upscale-image',
        media: `${HOME}/img-hummingbird.webp`,
      },
    ],
  },
  {
    label: 'Video',
    Icon: FilmIcon,
    tools: [
      {
        name: 'Video Agent',
        description: 'One sentence in, a finished video out',
        href: '/create-video',
        media: `${VIDEO}/filmset.mp4`,
        poster: `${VIDEO}/filmset-poster.jpg`,
        video: true,
      },
      {
        name: 'AI Video Generator',
        description: 'Cinematic clips from text or images',
        href: '/ai-video-generator',
        media: `${VIDEO}/glacier.mp4`,
        poster: `${VIDEO}/glacier-poster.jpg`,
        video: true,
      },
      {
        name: 'Talking Videos',
        description: 'Lip-synced presenters from a photo',
        href: '/ai-video-generator/talking',
        media: `${ASSETS}/news-anchor.mp4`,
        poster: `${HOME}/news-anchor-poster.jpg`,
        video: true,
      },
      {
        name: 'Motion Control',
        description: 'Transfer motion from any video',
        href: '/ai-video-generator/seedance-2',
        media: `${ASSETS}/motion-control-800-v2.mp4`,
        poster: `${HOME}/motion-control-800-v2-poster.jpg`,
        video: true,
      },
      {
        name: 'Animate Image',
        description: 'Bring still images to life',
        href: '/ai-animate-image',
        media: `${HOME}/vid-butterfly.mp4`,
        poster: `${HOME}/vid-butterfly-poster.jpg`,
        video: true,
      },
      {
        name: 'Video Editor',
        description: 'Timeline editing with AI assists',
        href: '/ai-video-editor',
        media: `${HOME}/vid-perfume.mp4`,
        poster: `${HOME}/vid-perfume-poster.jpg`,
        video: true,
      },
    ],
  },
  {
    label: 'Audio',
    Icon: VolumeUpIcon,
    tools: [
      {
        name: 'Text to Speech',
        description: 'Lifelike voices in 30+ languages',
        href: '/text-to-speech',
        media: `${HOME}/audio-tts.webp`,
      },
      {
        name: 'Voice Changer',
        description: 'Any recording, a different voice',
        href: '/voice-changer',
        media: `${HOME}/audio-voice.webp`,
      },
      {
        name: 'Music Generator',
        description: 'Full songs from a text prompt',
        href: '/ai-music-generator',
        media: `${HOME}/audio-music.webp`,
      },
      {
        name: 'AI Transcription',
        description: 'Record or upload — transcribe and summarize',
        href: '/ai-transcription',
        media: `${HOME}/audio-notes.webp`,
      },
      {
        name: 'AI Dubbing',
        description: 'Your videos, speaking 30+ languages',
        href: '/dubbing',
        media: `${HOME}/audio-dubbing.webp`,
      },
      {
        name: 'Sound Effects',
        description: 'Custom SFX from a description',
        href: '/ai-sound-effects-generator',
        media: `${HOME}/audio-sfx.webp`,
      },
    ],
  },
  {
    label: 'More',
    Icon: PlusCircleIcon,
    tools: [
      {
        name: 'Magic AI Tool',
        description: 'Describe a tool — get your own custom generator',
        href: '/presets/dynamic',
        media: `${HOME}/more-magic.webp`,
      },
      {
        name: 'Brand Voice',
        description: 'AI that writes the way your brand sounds',
        href: '/voices',
        media: `${HOME}/more-brandvoice.webp`,
      },
      {
        name: 'MCP & API',
        description: 'Build on Easy-Peasy from your own stack',
        href: '/mcp',
        media: `${HOME}/more-api.webp`,
      },
      {
        name: 'Easy-Peasy for Teams',
        description: 'Shared workspace, billing and admin controls',
        href: '/b2b',
        media: `${HOME}/more-teams.webp`,
      },
      {
        name: 'AI Workshops',
        description: 'Hands-on training, live in Singapore',
        href: '/ai-workshops',
        media: `${HOME}/more-workshop.webp`,
      },
    ],
  },
];

const ToolsShowcase = () => {
  const { t } = useLocale('home');
  const [activeIndex, setActiveIndex] = useState(0);

  return (
    <section className='relative py-20 lg:py-28'>
      <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
        <div className='mb-12 text-center'>
          <h2 className='text-4xl font-extrabold tracking-tight text-white md:text-5xl'>
            {t('Pick a tool, start creating')}
          </h2>
          <p className='mx-auto mt-4 max-w-2xl text-lg text-slate-400'>
            {t('One subscription unlocks the entire studio.')}
          </p>
        </div>

        <div className='flex flex-col gap-8 lg:flex-row lg:gap-12'>
          {/* Category rail — vertical on desktop, scrollable pills on mobile */}
          <div className='flex shrink-0 gap-2 overflow-x-auto pb-1 lg:w-52 lg:flex-col lg:overflow-visible lg:pb-0'>
            {CATEGORIES.map((category, index) => {
              const isActive = index === activeIndex;
              return (
                <button
                  key={category.label}
                  type='button'
                  onClick={() => setActiveIndex(index)}
                  className={`flex shrink-0 items-center gap-3 rounded-full px-5 py-3 text-left font-bold transition-all lg:rounded-xl ${
                    isActive
                      ? 'bg-indigo-500 text-white shadow-[0_8px_30px_-8px_rgba(99,102,241,0.7)]'
                      : 'border border-white/10 bg-white/[0.04] text-slate-300 hover:border-white/25 hover:text-white lg:border-transparent lg:bg-transparent'
                  }`}
                >
                  <category.Icon
                    className='h-5 w-5 shrink-0'
                    aria-hidden='true'
                  />
                  {t(category.label)}
                </button>
              );
            })}
          </div>

          {/* Panels — every category stays in the DOM so tool links are
              crawlable; only the active one is visible. */}
          <div className='min-w-0 flex-1'>
            {CATEGORIES.map((category, index) => (
              <div
                key={category.label}
                className={
                  index === activeIndex
                    ? 'tools-panel-in grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3'
                    : 'hidden'
                }
              >
                {category.tools.map((tool) => (
                  <Link
                    key={tool.href + tool.name}
                    href={tool.href}
                    className='group relative block h-56 overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04] transition-all hover:border-indigo-400/40 md:h-64'
                  >
                    {tool.video ? (
                      <AutoPlayVideo
                        src={tool.media}
                        poster={tool.poster}
                        className='absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]'
                      />
                    ) : (
                      // eslint-disable-next-line @next/next/no-img-element
                      <img
                        src={tool.media}
                        alt={t(tool.name)}
                        loading='lazy'
                        decoding='async'
                        className='absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]'
                      />
                    )}
                    <div className='absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent' />
                    <div className='absolute inset-x-0 bottom-0 p-5'>
                      <div className='flex items-center justify-between gap-2'>
                        <h3 className='font-bold text-white'>{t(tool.name)}</h3>
                        <span
                          aria-hidden='true'
                          className='flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/15 text-sm text-white opacity-0 backdrop-blur-md transition-all group-hover:opacity-100'
                        >
                          →
                        </span>
                      </div>
                      <p className='mt-0.5 text-sm leading-snug text-slate-300'>
                        {t(tool.description)}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>

      <style jsx global>{`
        @keyframes tools-panel-in {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .tools-panel-in {
          animation: tools-panel-in 0.35s ease-out both;
        }
        @media (prefers-reduced-motion: reduce) {
          .tools-panel-in {
            animation: none;
          }
        }
      `}</style>
    </section>
  );
};

export default ToolsShowcase;
